import React, {useEffect, useMemo, useState} from "react";
import _ from "lodash";
import {TFunction} from "i18next";
import {useTranslation} from "react-i18next";
import {useAtom, useAtomValue} from "jotai";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import {MultiPolygon} from "geojson";
import {Alert, Box, Button, Grid, Stack, Typography} from "@mui/material";
import {MaterialReactTable, MRT_ColumnDef, useMaterialReactTable} from "material-react-table";
import FmdGoodOutlinedIcon from "@mui/icons-material/FmdGoodOutlined";
import AddIcon from "@mui/icons-material/AddOutlined";
import {Dialog} from "../base/Dialog";
import {useFieldManagement} from "../../hooks/fieldManagementHooks";
import {Field, FieldPostBody} from "../../types/domain/field";
import {swallowException} from "../../utils/appUtils";
import {getResourceErrorMessage, isResourceFailed, isResourceSucceeded} from "../../utils/resourceUtils";
import {Frame} from "../base/Frame";
import {AutocompleteInput} from "../base/AutocompleteInput";
import {TextInput} from "../base/TextInput";
import {Order} from "../../types/domain/order";
import {FieldDrawMap} from "../map/FieldDrawMap";
import {FIELD_INITIAL_VALUES, FIELD_INITIAL_VALUES as INITIAL_FORM_VALUES} from "../../utils/constants";
import {FileDropZone} from "../base/FileDropZone";
import {WizardWelcomeDialog} from "./WizardWelcomeDialog";
import {BoundaryTypeOption} from "../../types/boundaryTypeOption";
import {authenticatedUserLoadable, authenticatedUserTableLocaleAtom} from "../../store/authenticatedUserStore";
import {isUserHaveAnyOfGlobalRoles} from "../../utils/accessControlUtils";
import {UserGlobalRole} from "../../types/domain/user";
import {FieldAttachments} from "../fields/FieldAttachments";
import {FieldStatus} from "../../types/domain/fieldStatus";
import {CropCycle} from "../../types/domain/cropCycle";
import {toCropCyclePost} from "../../utils/cropCycleUtils";
import {getDefaultMRTOptions} from "../base/BaseTable";
import {DatePickerInput} from "../base/DatePickerInput";
import {OrderStatus} from "../../types/domain/orderStatus";
import {CheckboxInput} from "../base/CheckboxInput";
import {OrderCreateDialog} from "../dialogs/OrderCreateDialog";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {useCompanyFarms} from "../../hooks/farmsHooks";
import {useOrders} from "../../hooks/ordersHooks";
import {LabeledInput} from "../base/LabeledInput";
import {FieldCreateFormInputType, getFieldCreateSchema} from "../../utils/fieldValidationUtils";
import {CropWidget} from "../fields/CropWidget";
import {SectionHeader} from "../base/SectionHeader";
import {constructValues, convertToHasIdWithName} from "../../utils/fieldCreateUtils";
import {useSoils} from "../../hooks/soilsHooks";
import {useIrrigations} from "../../hooks/irrigationsHooks";
import {useCrops} from "../../hooks/cropsHooks";
import {getIrrigationDefaultValue, IrrigationDataKeys} from "../../utils/irrigationDefaults";
import analytics from "../../services/analyticsService";
import {queryClient} from "../../utils/queryClient";

export interface Props {
    open: boolean;
    onClose: (isSuccessful: boolean) => void;
    mode: "new" | "edit";
    companyId: string;
    selectedField?: Field;
}

const getBoundaryStepTitle = (boundaryType: BoundaryTypeOption, t: TFunction<"translation", undefined>): string => {
    if (boundaryType === BoundaryTypeOption.MANUAL) {
        return t("fields.create_field_wizard.draw_boundaries_step_title");
    } else if (boundaryType === BoundaryTypeOption.UPLOAD_FILE) {
        return t("fields.create_field_wizard.upload_file_step_title");
    } else {
        return t("fields.create_field_wizard.draw_boundaries_step_title");
    }
};

export const FieldCreateDialog: React.FC<Props> = ({open, onClose, mode, companyId, selectedField}) => {
    const {t} = useTranslation();
    const [fields, setFields] = useState<Field[]>([]);
    const [farm, setFarm] = useState<{id: string} | undefined>();
    const [farmDialogOpened, setFarmDialogOpened] = useState<boolean>(false);

    const onFarmDialogClose = () => {
        setFarmDialogOpened(false);
    };

    const [orderDialogOpened, setOrderDialogOpened] = useState<boolean>(false);

    const onOrderEditDialogClose = () => {
        setOrderDialogOpened(false);
    };

    const [currentField, setCurrentField] = useState<Field | undefined>(selectedField);
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const isAdmin =
        isResourceSucceeded(authenticatedUserResource) &&
        isUserHaveAnyOfGlobalRoles(authenticatedUserResource.data, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);

    const initialStep = mode === "edit" ? 1 : 0;
    const [currentStep, setCurrentStep] = useState<number>(initialStep);
    const [boundaryType, setBoundaryType] = useState(BoundaryTypeOption.MANUAL);
    const fieldManagement = useFieldManagement();
    const {soils} = useSoils();
    const {irrigations} = useIrrigations();
    const {crops} = useCrops();

    let numberSteps: number, stepTitles: string[], stepContent: (() => React.JSX.Element)[];
    const isFinal = currentField && currentField.status === FieldStatus.FINAL;
    //For final field we don't show first step with the map.
    if (isFinal) {
        numberSteps = 1;
        stepTitles = [t("fields.create_field_wizard.field_details")];
        stepContent = [() => renderStepTwo()];
    } else {
        numberSteps = 2;
        stepTitles = [getBoundaryStepTitle(boundaryType, t), t("fields.create_field_wizard.field_details")];
        stepContent = [() => renderStepOne(), () => renderStepTwo()];
    }

    const [attachments, setAttachments] = useState<File[]>([]);

    const nextStep = () => {
        setCurrentStep((prevStep) => (prevStep % numberSteps) + 1);
    };

    const prevStep = () => {
        setCurrentStep((prevStep) => (prevStep % numberSteps) + 1);
    };

    const handleWizardWelcomeClick = (_boundaryType: BoundaryTypeOption) => {
        setBoundaryType(_boundaryType);
        setCurrentStep(1);
    };

    const [geometry, setGeometry] = useState<MultiPolygon | null>(currentField ? currentField.geometry : null);

    const [createdOrder, setCreatedOrder] = useState<{id: string} | undefined>(undefined);

    const farmsState = useCompanyFarms(companyId);
    const ordersState = useOrders(companyId);
    const allFarms = farmsState?.data?.farms;
    const [filteredOrders, setFilteredOrders] = useState<Order[]>([]);

    const initFormValues = useMemo(() => {
        return currentField ? constructValues(currentField) : INITIAL_FORM_VALUES;
    }, [currentField]);

    useEffect(() => {
        const _orders = ordersState.items.filter((item) => item.state === OrderStatus.NEW);
        // Ensure the currently selected order is included in the options
        if (
            initFormValues.orderId &&
            ordersState.items.findIndex((order) => order.id === initFormValues.orderId) >= 0
        ) {
            const currentOrder = ordersState.items.find((order) => order.id === initFormValues.orderId);
            if (currentOrder) {
                _orders.push(currentOrder);
            }
        }
        const newOrdersIds = _orders.map((item) => item.id);
        const currentOrdersIds = filteredOrders.map((item) => item.id);
        if (_orders.length > 0 && !_.isEqual(newOrdersIds, currentOrdersIds)) {
            setFilteredOrders(_orders);
        }
    }, [ordersState.items, companyId, filteredOrders, initFormValues.orderId]);

    const {
        control,
        handleSubmit,
        register,
        reset,
        setValue,
        formState: {errors},
        watch,
    } = useForm<FieldCreateFormInputType>({
        defaultValues: initFormValues,
        resolver: zodResolver(getFieldCreateSchema(t)),
    });

    //If a new farm was created, we set in control, via setValue method of useForm hook
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        void queryClient.invalidateQueries({queryKey: ["companyFarms", companyId]});
        setValue("farmId", farm ? farm.id : null);
    }, [farm, setValue]);

    //If a new order was created, we set in control, via setValue method of useForm hook
    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        ordersState.refresh();
        setValue("orderId", createdOrder ? createdOrder.id : null);
    }, [createdOrder, setValue]);

    const constructFieldPostBody = (inputs: FieldCreateFormInputType): FieldPostBody => {
        const priority = inputs.priority === true ? 1 : 0;
        const field: FieldPostBody = {
            soilId: inputs.soilId,
            irrigationTypeId: inputs.irrigationTypeId,
            name: inputs.name,
            priority: priority,
            irrigationInterval: inputs.irrigationInterval,
            desiredMoistureLevel: inputs.desiredMoistureLevel,
            irrigationCriteria: inputs.irrigationCriteria,
            irrigationDefault: inputs.irrigationDefault,
            satSoilMoistBias: inputs.satSoilMoistBias,
            KSATBias: inputs.KSATBias,
            farmId: inputs.farmId,
            orderId: inputs.orderId,
            //Values not coming from inputs.
            geometry: null,
            cropCycles: inputs.cropCycles.map((item) => toCropCyclePost(item)),
            onFarmEfficiencyMin: inputs.onFarmEfficiencyMin,
            minAppliedWater: inputs.minAppliedWater,
            maxAppliedWater: inputs.maxAppliedWater,
        };
        field.geometry = geometry;
        return field;
    };

    useEffect(() => {
        if (fields.length > 0) {
            setCurrentField(fields[0]);
            setGeometry(fields[0].geometry);
        }
    }, [fields, setCurrentField]);
    useEffect(() => {
        reset(initFormValues);
    }, [initFormValues, reset]);

    const onSubmit = (data: FieldCreateFormInputType): void => {
        analytics
            .track("field-create", {
                variation: "map",
                fields: 1,
            })
            .catch(swallowException);

        const fieldBody = constructFieldPostBody(data);
        const apiCall =
            mode === "edit" && currentField
                ? fieldManagement.edit(companyId, currentField.id, fieldBody)
                : fieldManagement.create(companyId, fieldBody);

        apiCall
            .then((res) => {
                const createdField = res;
                const id = createdField.id;

                if (attachments.length > 0) {
                    const formData = new FormData();
                    attachments.forEach((file) => {
                        formData.append("attachments", file, file.name);
                    });
                    fieldManagement
                        .uploadAttachments(companyId, {fieldId: id}, formData)
                        .then(() => {
                            onClose(true);
                        })
                        .catch(swallowException);
                } else {
                    onClose(true);
                }
            })
            .catch(swallowException);
    };
    const onDelete = () => {
        fieldManagement.reset();
        if (currentField?.id) {
            fieldManagement
                .delete(companyId, currentField.id)
                .then(() => {
                    onClose(true);
                })
                .catch(swallowException);
        } else {
            onClose(true);
        }
    };
    const handleAddNewCropCycle = () => {
        const updatedCropCycles = currentField ? currentField.cropCycles : [];
        updatedCropCycles.push({...FIELD_INITIAL_VALUES.cropCycles[0]});
        const updatedField = {...currentField!, cropCycles: updatedCropCycles};
        setCurrentField(updatedField);
    };
    const RenderCropWidgetTable: React.FC<{cropCycles: CropCycle[]}> = ({cropCycles}) => {
        const columns: MRT_ColumnDef<CropCycle>[] = [
            {
                header: t("dialogs.field_create.crop_cycle.crop"),
                size: 150,
                maxSize: 150,
                Cell: ({row}) => {
                    return (
                        <AutocompleteInput
                            //useForm supports FieldPath<TFieldValues> array values are handled with . not with []
                            name={`cropCycles.${row.index}.cropId`}
                            control={control}
                            options={crops}
                            disableClear
                            initialValue={cropCycles[row.index].cropId}
                            getOptionLabel={(option) => option.name}
                        />
                    );
                },
            },
            {
                accessorKey: "variety",
                size: 70,
                maxSize: 90,
                header: t("dialogs.field_create.crop_cycle.variety"),
                Cell: ({row}) => {
                    return (
                        <TextInput
                            name={`cropCycles.${row.index}.variety`}
                            type="text"
                            register={register}
                            errors={errors}
                        />
                    );
                },
            },
            {
                header: t("dialogs.field_create.crop_cycle.planted_date"),
                size: 140,
                maxSize: 140,
                Cell: ({row}) => {
                    return (
                        <DatePickerInput
                            name={`cropCycles.${row.index}.plantedDate`}
                            errors={errors}
                            control={control}
                        />
                    );
                },
            },
            {
                header: t("dialogs.field_create.crop_cycle.harvested_date"),
                size: 140,
                maxSize: 140,
                Cell: ({row}) => {
                    return (
                        <DatePickerInput
                            name={`cropCycles.${row.index}.estHarvestDate`}
                            errors={errors}
                            control={control}
                        />
                    );
                },
            },
            {
                header: t("dialogs.field_create.form.drought_sensitivity"),
                size: 40,
                maxSize: 40,
                muiTableHeadCellProps: {
                    size: "small",
                },
                Cell: ({row}) => {
                    return (
                        <TextInput
                            name={`cropCycles.${row.index}.droughtSensitivity`}
                            type="number"
                            register={register}
                            errors={errors}
                        />
                    );
                },
            },
            {
                header: t("dialogs.field_create.crop_cycle.eff_root_depth"),
                size: 40,
                maxSize: 40,
                muiTableHeadCellProps: {
                    size: "small",
                },
                Cell: ({row}) => {
                    return (
                        <TextInput
                            name={`cropCycles.${row.index}.effRootDepth`}
                            type="number"
                            register={register}
                            errors={errors}
                        />
                    );
                },
            },
        ];
        const adminColumns: MRT_ColumnDef<CropCycle>[] = [
            {
                header: t("dialogs.field_create.crop_cycle.reset_crop_cycle"),
                size: 50,
                maxSize: 50,
                Cell: ({row}) => {
                    return (
                        <CheckboxInput
                            name={`cropCycles.${row.index}.resetCropCycle`}
                            control={control}
                            initialValue={true}
                            label=""
                        />
                    );
                },
            },
            {
                header: t("dialogs.field_create.crop_cycle.reset_ET_applied_water"),
                size: 50,
                maxSize: 50,
                Cell: ({row}) => {
                    return (
                        <CheckboxInput
                            name={`cropCycles.${row.index}.resetETAppliedWater`}
                            control={control}
                            initialValue={true}
                            label=""
                        />
                    );
                },
            },
            {
                header: t("dialogs.field_create.crop_cycle.reset_root_depth"),
                size: 30,
                maxSize: 30,
                Cell: ({row}) => {
                    return (
                        <CheckboxInput
                            name={`cropCycles.${row.index}.resetRootDepth`}
                            control={control}
                            initialValue={true}
                            label=""
                        />
                    );
                },
            },
        ];
        if (isAdmin) {
            columns.push(...adminColumns);
        }

        const [locale] = useAtom(authenticatedUserTableLocaleAtom);
        const defaultTableOptions = getDefaultMRTOptions<CropCycle>(locale);
        const table = useMaterialReactTable({
            ...defaultTableOptions,
            columns,
            data: cropCycles,
            initialState: {
                ...defaultTableOptions.initialState,
                showColumnFilters: false,
                density: "compact",
            },
            enableExpandAll: false,
            enableExpanding: false,
            enablePagination: false,
            enableTopToolbar: true,
            enableBottomToolbar: false,
            positionPagination: "none",
            enableRowActions: false,
            muiTableHeadCellProps: {
                sx: {
                    fontsize: "0.8rem",
                    textTransform: "uppercase",
                    fontWeight: "400px",
                },
            },
            muiTableBodyCellProps: {
                sx: {
                    padding: "4px",
                },
            },
            renderTopToolbarCustomActions: () => (
                <Box sx={{display: "flex", justifyContent: "flex-end"}}>
                    <Button color="primary" startIcon={<AddIcon />} onClick={handleAddNewCropCycle}>
                        {t("dialogs.field_create.crop_widget.plant_new_crop_button")}
                    </Button>
                </Box>
            ),
        });
        return (
            <Grid item xs={12}>
                <Box
                    border={1}
                    borderRadius={1}
                    sx={{
                        borderColor: "secondary.main",
                    }}
                >
                    <MaterialReactTable table={table} />
                </Box>
            </Grid>
        );
    };
    const renderStepOne = () => {
        return (
            <>
                {boundaryType === BoundaryTypeOption.MANUAL ? (
                    <Box sx={{padding: "0rem"}}>
                        {isAdmin && currentField?.id && (
                            <FieldAttachments
                                companyId={companyId}
                                query={{fieldId: currentField.id}}
                            ></FieldAttachments>
                        )}
                        <FieldDrawMap setGeometry={setGeometry} geometry={geometry} mode={mode} setFields={setFields} />
                    </Box>
                ) : (
                    <Box sx={{padding: "1.5rem"}}>
                        <FileDropZone files={attachments} setFiles={setAttachments} />
                    </Box>
                )}
            </>
        );
    };
    const renderStepTwo = () => {
        const watchedIrrTypeId = watch("irrigationTypeId");
        const calculatedValue = (value: IrrigationDataKeys) => {
            return watchedIrrTypeId !== "0" ? getIrrigationDefaultValue(parseInt(watchedIrrTypeId), value) : "";
        };

        return (
            <Box sx={{padding: "1.5rem"}}>
                <form>
                    {isResourceFailed(fieldManagement.resource) && (
                        <Alert severity="error">{getResourceErrorMessage(fieldManagement.resource)}</Alert>
                    )}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <LabeledInput label={t("dialogs.field_create.form.field_name")}>
                                <TextInput name="name" type="text" register={register} errors={errors} />
                            </LabeledInput>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack direction="column">
                                <Typography fontSize={"0.75rem"} textTransform="uppercase" color={"#737990"}>
                                    {t("dialogs.field_create.form.farm")}
                                </Typography>
                                <Stack direction="row" gap="1rem" alignItems="center">
                                    <AutocompleteInput
                                        name="farmId"
                                        control={control}
                                        options={allFarms ?? []}
                                        noOptionsText={t("dialogs.field_create.form.no_farm_options")}
                                        disableClear
                                        initialValue={initFormValues.farmId}
                                        getOptionLabel={(option) => option.name}
                                    />
                                    <Button
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setFarmDialogOpened(true);
                                        }}
                                        sx={{
                                            flexGrow: 0,
                                        }}
                                    >
                                        {t("dialogs.field_create.form.new_button")}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                        <Grid item xs={6}>
                            <Stack direction="column">
                                <Typography fontSize={"0.75rem"} textTransform="uppercase" color={"#737990"}>
                                    {t("dialogs.field_create.form.order")}
                                </Typography>
                                <Stack direction="row" gap="1rem" alignItems="center">
                                    <AutocompleteInput
                                        name="orderId"
                                        control={control}
                                        options={filteredOrders}
                                        noOptionsText={t("dialogs.field_create.form.no_order_options")}
                                        disableClear
                                        initialValue={initFormValues.orderId}
                                        getOptionLabel={(option) => option.id}
                                        disabled={isFinal}
                                    />
                                    <Button
                                        color="primary"
                                        startIcon={<AddIcon />}
                                        onClick={() => {
                                            setOrderDialogOpened(true);
                                        }}
                                        sx={{
                                            flexGrow: 0,
                                        }}
                                    >
                                        {t("dialogs.field_create.form.new_button")}
                                    </Button>
                                </Stack>
                            </Stack>
                        </Grid>
                        {initFormValues.cropCycles && initFormValues.cropCycles.length > 1 ? (
                            <RenderCropWidgetTable cropCycles={initFormValues.cropCycles} />
                        ) : (
                            <CropWidget
                                mode={mode}
                                t={t}
                                crops={crops}
                                control={control}
                                register={register}
                                errors={errors}
                                handleAddNewCropCycle={handleAddNewCropCycle}
                                initFormValues={initFormValues}
                                isAdmin={isAdmin}
                            />
                        )}
                        <Grid item xs={12}>
                            <Frame>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <SectionHeader
                                            label={t("dialogs.field_create.form.irrigation_section_title")}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_type")}>
                                            <AutocompleteInput
                                                name="irrigationTypeId"
                                                control={control}
                                                options={irrigations}
                                                disableClear
                                                initialValue={initFormValues.irrigationTypeId}
                                                getOptionLabel={(option) => option.name}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_interval")}>
                                            <AutocompleteInput
                                                name="irrigationInterval"
                                                control={control}
                                                options={convertToHasIdWithName([1, 2, 3, 4, 5, 6, 7, 8])}
                                                initialValue={initFormValues.irrigationInterval}
                                                getOptionLabel={(option) => option.name}
                                                placeholder={calculatedValue("irrigation_rotation_default") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.moisture_level")}>
                                            <TextInput
                                                name="desiredMoistureLevel"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("desirable_storage") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_criteria")}>
                                            <TextInput
                                                name="irrigationCriteria"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("irrigation_criterium_min") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.irrigation_default")}>
                                            <TextInput
                                                name="irrigationDefault"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("irrigation_default") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.on_farm_efficiency_min")}>
                                            <TextInput
                                                name="onFarmEfficiencyMin"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("on_farm_efficiency_min") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.min_applied_water")}>
                                            <TextInput
                                                name="minAppliedWater"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("min_applied_water") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={4}>
                                        <LabeledInput label={t("dialogs.field_create.form.max_applied_water")}>
                                            <TextInput
                                                name="maxAppliedWater"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                                placeholder={calculatedValue("max_applied_water") as string}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            </Frame>
                        </Grid>

                        <Grid item xs={12}>
                            <Frame>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <SectionHeader label={t("dialogs.field_create.form.soil_section_title")} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <LabeledInput label={t("dialogs.field_create.form.soil_type")}>
                                            <AutocompleteInput
                                                name="soilId"
                                                control={control}
                                                options={soils}
                                                disableClear
                                                initialValue={initFormValues.soilId}
                                                getOptionLabel={(option) => option.name}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                    <Grid item xs={6} sx={!isAdmin ? visuallyHidden : {}}>
                                        <LabeledInput label={t("dialogs.field_create.form.sat_soil_moisture_bias")}>
                                            <TextInput
                                                name="satSoilMoistBias"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>

                                    <Grid item xs={6} sx={!isAdmin ? visuallyHidden : {}}>
                                        <LabeledInput label={t("dialogs.field_create.form.ksat_bias")}>
                                            <TextInput
                                                name="KSATBias"
                                                type="number"
                                                register={register}
                                                errors={errors}
                                            />
                                        </LabeledInput>
                                    </Grid>
                                </Grid>
                            </Frame>
                        </Grid>
                    </Grid>
                </form>
                {orderDialogOpened && companyId && (
                    <OrderCreateDialog
                        mode="new"
                        companyId={companyId}
                        open={orderDialogOpened}
                        onClose={onOrderEditDialogClose}
                        setOrder={setCreatedOrder}
                    />
                )}
                {farmDialogOpened && companyId && (
                    <FarmCreateDialog
                        mode="new"
                        companyId={companyId}
                        open={farmDialogOpened}
                        onClose={onFarmDialogClose}
                        setFarm={setFarm}
                    />
                )}
            </Box>
        );
    };
    return (
        <>
            {currentStep === 0 ? (
                <WizardWelcomeDialog
                    open={true}
                    onClose={() => onClose(false)}
                    handleClick={handleWizardWelcomeClick}
                />
            ) : (
                <Dialog
                    enableSubmit={currentStep === numberSteps}
                    enableDelete={mode === "edit"}
                    deleteTitle={t("dialogs.actions.delete")}
                    submitTitle={t("dialogs.actions.submit")}
                    title={
                        mode === "edit" ? t("dialogs.field_create.edit_title") : t("dialogs.field_create.create_title")
                    }
                    titleIcon={<FmdGoodOutlinedIcon color="primary" />}
                    open={open}
                    deleteConfirmation={t("dialogs.field_create.delete_confirmation")}
                    disableDefaultClose={true}
                    onClose={() => onClose(false)}
                    onDelete={onDelete}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onSubmit={handleSubmit(onSubmit)}
                    width={"900px"}
                    padding={"0rem"}
                    wizzardProps={{
                        currentStep,
                        nextStep,
                        prevStep,
                        steps: numberSteps,
                        stepsTitles: stepTitles,
                    }}
                >
                    <>
                        {stepContent.map((contentFunc, index) => {
                            //Step 0 is the Welcome wizzard we handle it outside the Wizard.
                            if (currentStep === index + 1) {
                                return <div key={index}>{contentFunc()}</div>;
                            }
                            return null;
                        })}
                    </>
                </Dialog>
            )}
        </>
    );
};
const visuallyHidden = {
    position: "absolute",
    height: "1px",
    width: "1px",
    overflow: "hidden",
    whiteSpace: "nowrap",
};
